import { useState, useEffect } from 'react';
import { useTheme } from '../ThemeContext';
import { useTranslation } from 'react-i18next';

interface SplashScreenProps {
  onEnter: () => void;
  onDemo: () => void;
}

const SplashScreen = ({ onEnter, onDemo }: SplashScreenProps) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const isDark = theme === 'dark';

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setShowContent(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative min-h-screen flex flex-col items-center justify-center overflow-hidden transition-colors duration-500 ${isDark ? 'bg-[#08090F]' : 'bg-[#F8F9FB]'}`}>
      {isDark ? (
        <>
          <div className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse" style={{ background: 'radial-gradient(circle, #00d4ff, transparent)', top: '-10%', left: '-10%' }} />
          <div className="absolute w-80 h-80 rounded-full opacity-15 blur-3xl animate-pulse" style={{ background: 'radial-gradient(circle, #a78bfa, transparent)', bottom: '10%', right: '-5%', animationDelay: '1s' }} />
          <div className="absolute w-64 h-64 rounded-full opacity-10 blur-2xl animate-pulse" style={{ background: 'radial-gradient(circle, #00d4ff, transparent)', bottom: '30%', left: '20%', animationDelay: '2s' }} />
        </>
      ) : (
        <>
          <div className="absolute w-96 h-96 rounded-full opacity-15 blur-3xl animate-pulse" style={{ background: 'radial-gradient(circle, #0070E0, transparent)', top: '-10%', right: '-10%' }} />
          <div className="absolute w-80 h-80 rounded-full opacity-10 blur-3xl animate-pulse" style={{ background: 'radial-gradient(circle, #F59E0B, transparent)', bottom: '10%', left: '-5%', animationDelay: '1.5s' }} />
        </>
      )}
      {isDark && (
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      )}

      <div className={`relative z-10 flex flex-col items-center px-8 text-center transition-all duration-700 ${mounted && showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="mb-6 relative">
          <div className={`absolute inset-0 rounded-full blur-2xl opacity-40 ${isDark ? 'bg-[#00d4ff]' : 'bg-[#0070E0]'}`} style={{ transform: 'scale(1.2)' }} />
          <img src="https://public.readdy.ai/ai/img_res/453cee12-da90-40e3-bb2b-675a79907aa9.png" alt="ExpoFlow AI" className="relative w-20 h-20 object-contain" />
        </div>

        <h1 className={`text-4xl font-black tracking-tight mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Expo<span className={isDark ? 'text-[#00d4ff]' : 'text-[#0070E0]'}>Flow</span>{' '}
          <span className={`text-2xl font-semibold ${isDark ? 'text-[#a78bfa]' : 'text-amber-500'}`}>AI</span>
        </h1>

        <p className={`text-base font-medium mb-2 tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {t('splash.tagline')}
        </p>

        <div className={`flex gap-2 mt-4 mb-12 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '200ms' }}>
          {(['splash.tag.ai', 'splash.tag.realtime', 'splash.tag.nav'] as const).map((key) => (
            <span key={key} className={`text-[10px] font-semibold px-3 py-1 rounded-full border ${isDark ? 'border-[#00d4ff]/30 text-[#00d4ff] bg-[#00d4ff]/10' : 'border-[#0070E0]/30 text-[#0070E0] bg-[#0070E0]/10'}`}>
              {t(key)}
            </span>
          ))}
        </div>

        <div className={`w-64 h-40 rounded-2xl mb-12 overflow-hidden relative transition-all duration-700 ${showContent ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{ transitionDelay: '400ms' }}>
          <img src="https://readdy.ai/api/search-image?query=futuristic%20AI%20expo%20navigation%20holographic%20map%20interface%20with%20glowing%20neon%20lines%20and%20digital%20booth%20grid%20overlay%20on%20dark%20background%20premium%20tech%20visualization&width=512&height=320&seq=splash001&orientation=landscape" alt="ExpoFlow visualization" className="w-full h-full object-cover object-top" />
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-t from-[#08090F] via-transparent to-transparent' : 'bg-gradient-to-t from-[#F8F9FB] via-transparent to-transparent'}`} />
        </div>

        <div className={`flex gap-8 mb-12 transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '500ms' }}>
          {([['12K+', 'splash.stat.expos'], ['2.4M', 'splash.stat.booths'], ['98%', 'splash.stat.accuracy']] as const).map(([val, key]) => (
            <div key={key} className="flex flex-col items-center">
              <span className={`text-xl font-black ${isDark ? 'text-white' : 'text-gray-900'}`}>{val}</span>
              <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{t(key)}</span>
            </div>
          ))}
        </div>

        <div className={`flex flex-col gap-3 w-full max-w-xs transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '600ms' }}>
          <button onClick={onEnter} className={`w-full py-4 rounded-2xl font-bold text-base tracking-wide transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap ${isDark ? 'bg-gradient-to-r from-[#00d4ff] to-[#0099bb] text-[#08090F] shadow-[0_0_30px_rgba(0,212,255,0.4)]' : 'bg-gradient-to-r from-[#0070E0] to-[#0050A0] text-white shadow-[0_4px_20px_rgba(0,112,224,0.3)]'}`}>
            <span className="flex items-center justify-center gap-2"><i className="ri-rocket-line"></i>{t('splash.btn.enter')}</span>
          </button>
          <button onClick={onDemo} className={`w-full py-4 rounded-2xl font-semibold text-base tracking-wide transition-all duration-200 active:scale-95 cursor-pointer whitespace-nowrap border ${isDark ? 'border-[#1e2d47] text-gray-300 hover:bg-[#111827]' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
            <span className="flex items-center justify-center gap-2"><i className="ri-play-circle-line"></i>{t('splash.btn.demo')}</span>
          </button>
        </div>

        <p className={`mt-6 text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{t('splash.trusted')}</p>
      </div>
    </div>
  );
};

export default SplashScreen;
